// src/routes/PageLayoutRoute.tsx
import PageLayout from "../layouts/PageLayout";
import { Outlet, useLocation } from "react-router-dom";
import { useBrand } from "../modules/brands/contexts/BrandContext";
import { useStores } from "@modules/stores/hooks/useStores";
import { useProducts } from "@modules/products/hooks/useProducts";
import { useGymPartner } from "@modules/gyms/contexts/gym.context";
import { useGymBrands } from "@modules/gyms/hooks/useGymBrands";
import { useReport } from "@modules/reports/hooks/use-report.hook";
import { useEffect, useState } from "react";

const SEGMENT_LABEL_MAP: Record<string, string> = {
  brands: "Thương Hiệu",
  gyms: "Phòng Gym",
  stores: "Nhãn Hàng",
  trainers: "Trainers",
  "top-trainers": "Top Trainers",
  "trainer-verification": "Trainer Verification",
  "trainer-package-verification": "Trainer Packages",
  engagements: "Engagements",
  campaigns: "Campaigns",
  rewards: "Rewards",
  clients: "Clients",
  "admin-post": "Posts",
  "post-detail": "Post Detail",
  "onboarding-media": "Onboarding",
  "system-media": "System",
  "media-detail": "Media Detail",
  "term-policy": "Terms",
  "privacy-policy": "Privacy",
  report: "Báo cáo",
  "system-setting": "System Settings",
  "openAI-assistant": "OpenAI Assistant",
  mongodb: "MongoDB",
  vietguys: "VietGuys",
  "feature-flag": "Feature Flags",
};

const HIDDEN_SEGMENTS = new Set(["b2b", "products"]);

const isObjectId = (seg: string): boolean => /^[a-fA-F0-9]{24}$/.test(seg);

export default function PageLayoutRoute() {
  const location = useLocation();
  const { getBrandById } = useBrand();
  const { getStoreById, getShopById } = useStores();
  const { getProductById } = useProducts();
  const { branches } = useGymPartner();
  const { brands: gymBrands } = useGymBrands();
  const { getReportDetailById } = useReport();

  const [reportTitle, setReportTitle] = useState<string | null>(null);

  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    if (pathSegments[0] === "report" && isObjectId(pathSegments[1])) {
      const reportId = pathSegments[1];
      const stateLabel = (location.state as { breadcrumbLabel?: string } | null)
        ?.breadcrumbLabel;
      if (stateLabel) {
        setReportTitle(stateLabel);
        return;
      }
      getReportDetailById(reportId)
        .then((data) => {
          const display = data?.target_display || data?.target_id || "Chi tiết";
          setReportTitle(
            display.length > 40 ? display.slice(0, 40) + "..." : display,
          );
        })
        .catch(() => setReportTitle("Báo cáo"));
    } else {
      setReportTitle(null);
    }
  }, [location.pathname, location.state, getReportDetailById]);

  type BreadcrumbItem = {
    label: string;
    to?: string;
    active?: boolean;
  };

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const stateLabel = (location.state as { breadcrumbLabel?: string } | null)
    ?.breadcrumbLabel;

  const productsIndex = pathSegments.indexOf("products");
  const isProductDetail =
    productsIndex !== -1 &&
    productsIndex + 1 < pathSegments.length &&
    isObjectId(pathSegments[productsIndex + 1]) &&
    productsIndex - 1 >= 0 &&
    isObjectId(pathSegments[productsIndex - 1]) &&
    pathSegments[productsIndex - 2] === "stores";

  let breadcrumbs: BreadcrumbItem[] = [{ label: "Trang Chủ", to: "/" }];
  let pageTitle = "Trang Chủ";

  if (isProductDetail) {
    const storeIdFromUrl = pathSegments[productsIndex - 1];
    const productId = pathSegments[productsIndex + 1];
    const product = getProductById(productId);
    const shop = product ? getShopById(product.shop_id) : undefined;

    let store = shop ? getStoreById(shop.b2b_id) : undefined;
    if (!store) {
      store = getStoreById(storeIdFromUrl);
    }

    const storeName = store?.name || "Nhãn Hàng";
    const shopName = shop?.name || "Chi Nhánh";
    const productName = product?.name || "Sản Phẩm";

    const storeLink = store ? `/b2b/stores/${store.id}` : undefined;
    const shopLink = store ? `/b2b/stores/${store.id}?tab=products` : undefined;

    breadcrumbs = [
      { label: "Trang Chủ", to: "/" },
      { label: storeName, to: storeLink },
      { label: shopName, to: shopLink },
      { label: productName, active: true },
    ];
    pageTitle = productName;
  } else {
    buildDefaultBreadcrumbs();
  }

  function buildDefaultBreadcrumbs() {
    let detailLabel: string | undefined =
      stateLabel || reportTitle || undefined;
    const lastSeg = pathSegments[pathSegments.length - 1];

    const isBranchDetail =
      pathSegments.indexOf("branch") && lastSeg && isObjectId(lastSeg);
    if (isBranchDetail) {
      const branch = branches.find((b) => b.id === lastSeg);
      if (branch) {
        const brand = gymBrands.find((b) => b.id === branch.brandId);
        const brandName = brand?.name || "Phòng Gym";
        breadcrumbs = [
          { label: "Trang Chủ", to: "/" },
          { label: "Phòng Gym", to: "/b2b/gyms" },
          { label: brandName, to: `/b2b/gyms/${branch.brandId}` },
          { label: branch.name, active: true },
        ];
        pageTitle = branch.name;
        return;
      }
    }

    if (!detailLabel && lastSeg && isObjectId(lastSeg)) {
      const pathStr = location.pathname;
      if (
        pathStr.includes("/b2b/gyms/") &&
        (pathStr.includes("/branches/") || pathStr.includes("/branch/"))
      ) {
        const branch = branches.find((b) => b.id === lastSeg);
        if (branch) detailLabel = branch.name;
      } else if (
        pathStr.includes("/b2b/brands/") ||
        pathStr.includes("/b2b/gyms/") ||
        pathStr.includes("/b2b/gym-brand/")
      ) {
        const brand = getBrandById(lastSeg);
        if (brand) detailLabel = brand.name;
      } else if (pathStr.includes("/b2b/stores/")) {
        const store = getStoreById(lastSeg);
        if (store) detailLabel = store.name;
      }
    }

    const isDetailPage = !!detailLabel;
    const segmentsToIterate = isDetailPage
      ? pathSegments.slice(0, -1)
      : pathSegments;

    let accumulatedPath = "";
    for (let i = 0; i < segmentsToIterate.length; i++) {
      const seg = segmentsToIterate[i];
      accumulatedPath += "/" + seg;

      if (HIDDEN_SEGMENTS.has(seg)) continue;

      let label: string | undefined;
      let to: string = accumulatedPath;

      if (isObjectId(seg) && i > 0 && pathSegments[i - 1] === "report") {
        label = reportTitle || "Báo cáo";
      } else if (SEGMENT_LABEL_MAP[seg]) {
        label = SEGMENT_LABEL_MAP[seg];
        if (seg === "report") {
          to = "/report";
        }
      } else if (isObjectId(seg)) {
        const entityName = getEntityName(seg, i);
        if (entityName) {
          label = entityName;
        } else {
          const prevSeg = i > 0 ? pathSegments[i - 1] : null;
          if (prevSeg === "stores") label = "Store";
          else if (prevSeg === "products") label = "Product";
          else if (prevSeg === "brands" || prevSeg === "gyms") label = "Brand";
          else if (prevSeg === "branches" || prevSeg === "branch")
            label = "Branch";
          else label = "Item";
        }
      } else {
        continue;
      }

      const isLast = i === segmentsToIterate.length - 1;
      const isActive = !isDetailPage && isLast;

      breadcrumbs.push({
        label: label!,
        to: isActive ? undefined : to,
        active: isActive,
      });
    }

    if (detailLabel) {
      breadcrumbs.push({
        label: detailLabel,
        active: true,
      });
    }

    if (breadcrumbs.length === 1) {
      breadcrumbs[0].active = true;
    }

    const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
    pageTitle =
      lastBreadcrumb && lastBreadcrumb.label !== "Trang Chủ"
        ? lastBreadcrumb.label
        : "Trang Chủ";
  }

  function getEntityName(seg: string, index: number): string | undefined {
    if (!isObjectId(seg)) return undefined;

    const prevSeg = index > 0 ? pathSegments[index - 1] : null;
    const nextSeg =
      index < pathSegments.length - 1 ? pathSegments[index + 1] : undefined;

    if (prevSeg === "branches" || prevSeg === "branch") {
      const branch = branches.find((b) => b.id === seg);
      return branch?.name;
    }

    if (prevSeg === "brands" || prevSeg === "gyms" || prevSeg === "gym-brand") {
      const gymBrand = gymBrands.find((b) => b.id === seg);
      if (gymBrand) return gymBrand.name;
      const brand = getBrandById(seg);
      return brand?.name;
    }

    if (prevSeg === "stores" && nextSeg !== "products") {
      const store = getStoreById(seg);
      return store?.name;
    }

    if (prevSeg === "products") {
      const product = getProductById(seg);
      return product?.name;
    }

    return undefined;
  }

  return (
    <PageLayout title={pageTitle} breadcrumbs={breadcrumbs}>
      <Outlet />
    </PageLayout>
  );
}
